// WorkspaceTabs.js — turns the details view's own <Tab>s into the tab tray the
// clinician arranged: pinned items as tabs in their order, then the Apps tab
// holding everything unpinned. The views keep writing their Tab bodies inline;
// this only decides where each one goes. Rearranging lives in WorkspaceEdit.js:
// right-click a tab for its menu; in Edit layout the tabs jiggle and drag.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Tab } from 'react-bootstrap';
import AppsTabTitle from '../VendorApps/AppsTabTitle';
import VendorAppsPanel from '../VendorApps/VendorAppsPanel';
import AppsLauncher from './AppsLauncher';
import ItemGlyph from './ItemGlyph';
import { EditBadge, beginDrag, dragState, ensureJiggleStyle } from './WorkspaceEdit';
import { APPS_ID, LOCKED, idForTabKey, tabKeyFor } from '../../utils/workspace/workspaceLayout';

/**
 * A pinned tab's label. Right-click for its menu. In edit mode it jiggles and
 * drags; the tab bar as a whole is the drop zone (AppsTitle), re-flowing live
 * while something is dragged over it. A − sends it to Apps. When the bar is
 * compact and this tab isn't the one on screen, it shows only its icon (or
 * `compactTitle`). The ghost is where a dragged tab came from, until the drop.
 */
export function WorkspaceTabTitle({ id, index, workspace, active, compactTitle, children }) {
  const editing = workspace.editing;
  const item = workspace.byId[id];
  const compact = workspace.compact && !active;
  useEffect(() => {
    if (editing) ensureJiggleStyle();
  }, [editing]);
  const dragged = workspace.dragging === id;
  return (
    <span
      draggable={editing}
      data-workspace-tab={id}
      title={compact ? item?.label : undefined}
      aria-label={compact ? item?.label : undefined}
      className={editing && !dragged ? `cpt-jiggle${index % 2 ? ' cpt-jiggle-b' : ''}` : undefined}
      onContextMenu={(e) => workspace.showMenu(e, id)}
      onDragStart={(e) => (editing ? beginDrag(e, id, workspace) : e.preventDefault())}
      onDragEnd={() => workspace.endDrag()}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 22,
        lineHeight: 1,
        opacity: workspace.ghost === id ? 0.3 : dragged ? 0.55 : 1,
        transition: 'opacity .1s',
      }}
    >
      {editing && !LOCKED.includes(id) && <EditBadge kind="remove" label="Move to Apps" small={compact} onClick={() => workspace.setPinned(id, false)} />}
      {compact && item ? compactTitle || <ItemGlyph item={item} size={18} /> : children}
      {item?.attention && !editing && <AttentionDot item={item} patientName={workspace.patientName} />}
    </span>
  );
}

/**
 * A pinned app that isn't linked to the open patient yet: a dot in its colour
 * on its own tab, saying so on hover. Opening the tab shows the link prompt.
 */
function AttentionDot({ item, patientName }) {
  const who = patientName || 'this patient';
  const text = `Not linked to ${who} yet: open ${who} in ${item.label} to link.`;
  return (
    <span
      role="img"
      aria-label={text}
      title={text}
      style={{
        position: 'absolute',
        top: -4,
        right: -6,
        width: 9,
        height: 9,
        borderRadius: '50%',
        background: item.color,
        boxShadow: '0 0 0 2px #fff',
      }}
    />
  );
}

/** Edit mode only: the empty spot at the end of the tabs, so the bar reads as somewhere things can go. */
function PinSlot({ workspace }) {
  const from = workspace.dragging;
  const incoming = Boolean(from) && !workspace.isPinned(from);
  return (
    <span
      data-workspace-pin-slot
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        border: `1.5px dashed ${incoming ? '#5e72e4' : '#c3cadb'}`,
        borderRadius: 8,
        color: incoming ? '#5e72e4' : '#8898aa',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: '16px',
        whiteSpace: 'nowrap',
      }}
    >
      +{workspace.compact ? '' : ' Drop to pin'}
    </span>
  );
}

/**
 * The Apps tab label. Right-click for Edit layout. It also owns the tab bar as
 * a whole: whether the tabs fit (compact when they don't, measured before
 * paint so nothing flickers), the edit-mode outline, and the bar as ONE drop
 * zone. While something is dragged over it, the tabs re-flow live: the item
 * goes before or after the tab under the pointer (the side it is moving
 * toward, so neighbours don't jitter), past the last tab means the end, and
 * over Apps means back into the tray.
 */
function AppsTitle({ workspace, active }) {
  const ref = useRef(null);
  const fullWidth = useRef(0);
  const live = useRef(workspace);
  live.current = workspace;
  // The saved layout, not a drag's preview: re-measuring on every preview step would stutter.
  const signature = [workspace.savedPinnedIds.join(','), workspace.editing].join('|');
  const { compact, setCompact } = workspace;
  // Measuring pauses while a drag previews (it would stutter); the drop lands
  // while the drag is still on, so the end of the drag must re-measure.
  const dragging = Boolean(workspace.dragging);

  // A new set of tabs is measured from full width again — except mid-drag,
  // where the drop's new set is measured when the drag ends (no flash of
  // full-width tabs in between).
  useLayoutEffect(() => {
    if (live.current.dragging) return;
    setCompact(false);
  }, [signature, setCompact]);

  useLayoutEffect(() => {
    const nav = ref.current?.closest('.nav');
    if (!nav) return undefined;
    const check = () => {
      if (live.current.dragging) return;
      const tabs = Array.from(nav.querySelectorAll('[role="tab"]'));
      // Too wide to scroll, or wrapped onto a second row (the web app's tabs wrap).
      const overflowing = nav.scrollWidth > nav.clientWidth + 1 || tabs.some((t) => t.offsetTop > (tabs[0]?.offsetTop ?? 0) + 4);
      if (!compact && overflowing) {
        // What the full-width tabs need. Summed rather than scrollWidth: a
        // wrapping bar's scrollWidth is just its own width, and expanding on
        // that would flip compact on and off forever.
        const style = window.getComputedStyle(nav);
        const padding = parseFloat(style.paddingLeft || 0) + parseFloat(style.paddingRight || 0);
        fullWidth.current = tabs.reduce((sum, t) => sum + t.getBoundingClientRect().width, 0) + padding;
        setCompact(true);
      } else if (compact && fullWidth.current && nav.clientWidth >= fullWidth.current + 8) {
        setCompact(false);
      }
    };
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(check);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [compact, signature, setCompact, dragging]);

  // The whole bar is the drop zone (a disabled tab can't take a drop: its
  // link has pointer-events none, which jsdom never shows).
  useEffect(() => {
    const nav = ref.current?.closest('.nav');
    if (!nav) return undefined;
    const onOver = (e) => {
      const ws = live.current;
      const from = dragState.get();
      if (!ws.editing || !from) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      const tabEl = e.target?.closest?.('[role="tab"]');
      if (tabEl && ref.current && tabEl.contains(ref.current)) {
        ws.setPreview({ id: from, pinned: false });
        return;
      }
      const overId = tabEl?.querySelector('[data-workspace-tab]')?.dataset.workspaceTab;
      const list = ws.pinned.map((i) => i.id);
      if (!overId) {
        // Between tabs keeps the current spot; past the last one is the end.
        const lastId = list.filter((x) => x !== from).pop();
        const lastEl = lastId && Array.from(nav.querySelectorAll('[data-workspace-tab]')).find((n) => n.dataset.workspaceTab === lastId)?.closest('[role="tab"]');
        const pastEnd = !lastEl || e.clientX > lastEl.getBoundingClientRect().right;
        if (pastEnd || !ws.preview) ws.setPreview({ id: from, pinned: true });
        return;
      }
      if (overId === from) return;
      const fromAt = list.indexOf(from);
      const overAt = list.indexOf(overId);
      let after;
      if (fromAt !== -1 && ws.ghost !== from) after = fromAt < overAt;
      else {
        const rect = tabEl.getBoundingClientRect();
        after = e.clientX > rect.left + rect.width / 2;
      }
      ws.setPreview({ id: from, pinned: true, targetId: overId, after });
    };
    const onDrop = (e) => {
      if (!dragState.get()) return;
      e.preventDefault();
      live.current.commitPreview();
      // End the drag here, not on dragend: a tray tile that just became a tab
      // has left the page, so its dragend never arrives — and a drag left
      // "on" stops it jiggling and pauses the bar's measuring.
      live.current.endDrag();
    };
    nav.addEventListener('dragover', onOver);
    nav.addEventListener('drop', onDrop);
    return () => {
      nav.removeEventListener('dragover', onOver);
      nav.removeEventListener('drop', onDrop);
    };
  }, []);

  useEffect(() => {
    const nav = ref.current?.closest('.nav');
    if (!nav) return;
    nav.classList.toggle('cpt-tabs-compact', compact);
    nav.classList.toggle('cpt-tabs-editing', workspace.editing);
    nav.classList.toggle('cpt-tabs-dropping', workspace.editing && Boolean(workspace.dragging) && !workspace.isPinned(workspace.dragging));
  });

  const unpinning = workspace.editing && workspace.preview && workspace.preview.pinned === false && workspace.isPinned(workspace.preview.id);
  return (
    <span
      ref={ref}
      onContextMenu={(e) => workspace.showMenu(e, APPS_ID)}
      title={compact && !active ? 'Apps' : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', minHeight: 22, lineHeight: 1, borderRadius: 6, boxShadow: unpinning ? '0 0 0 2px #5e72e4' : undefined }}
    >
      <AppsTabTitle compact={compact && !active} only={workspace.unpinned.filter((i) => i.kind === 'vendor').map((i) => i.appId)} />
    </span>
  );
}

function VendorTabTitle({ item }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <ItemGlyph item={item} />
      <span style={{ fontSize: '1rem', fontWeight: 500 }}>{item.label}</span>
    </span>
  );
}

/**
 * @param workspace     useVisitWorkspace()
 * @param tabs          the view's <Tab eventKey=…> elements for built-ins (falsy entries ignored)
 * @param activeTab     the Tabs activeKey
 * @param compactTitles what a built-in shows when the bar is compact, if not its
 *                      icon — e.g. { compliance: <the grade letter> }
 */
export function renderWorkspaceTabs(workspace, tabs, activeTab, compactTitles = {}) {
  const builtin = {};
  React.Children.toArray(tabs.filter(Boolean)).forEach((tab) => {
    builtin[idForTabKey(tab.props.eventKey)] = tab;
  });

  const bodyFor = (item) => {
    if (item.kind === 'vendor') return <VendorAppsPanel appId={item.appId} active={workspace.isShown(item.id)} />;
    return builtin[item.id]?.props.children ?? null;
  };

  const pinnedTabs = workspace.pinned
    .filter((item) => item.kind === 'vendor' || builtin[item.id])
    .map((item, index) => {
      const title = (
        <WorkspaceTabTitle
          id={item.id}
          index={index}
          workspace={workspace}
          active={activeTab === tabKeyFor(item.id)}
          compactTitle={compactTitles[item.id]}
        >
          {item.kind === 'vendor' ? <VendorTabTitle item={item} /> : builtin[item.id].props.title}
        </WorkspaceTabTitle>
      );
      if (item.kind === 'vendor') {
        return (
          <Tab key={item.id} eventKey={tabKeyFor(item.id)} title={title}>
            {bodyFor(item)}
          </Tab>
        );
      }
      return React.cloneElement(builtin[item.id], { key: item.id, title });
    });

  return [
    ...pinnedTabs,
    workspace.editing && <Tab key="__pin-slot" eventKey="__pin-slot" disabled title={<PinSlot workspace={workspace} />} />,
    <Tab key={APPS_ID} eventKey={APPS_ID} title={<AppsTitle workspace={workspace} active={activeTab === APPS_ID} />}>
      <AppsLauncher workspace={workspace} active={activeTab === APPS_ID} renderBody={bodyFor} />
    </Tab>,
  ].filter(Boolean);
}
